import { createContext, useContext, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useInfiniteReportGroupAssets } from '@/hooks/api/reports/useInfiniteReportGroupAssets';
import { ReportGroupAsset } from '@/hooks/api/reports/useListReportGroupAssets';

const ASSET_ID_PARAM = 'referenceAssetId';

interface ReportMasterDetailContextValue {
  selectedAssetId: string | undefined;
  setSelectedAssetId: (id: string | undefined) => void;
  hasMatchData: boolean;
  allAssets: ReportGroupAsset[];
  allAssetsTotal: number;
  isLoadingAllAssets: boolean;
  allAssetsError: Error | undefined;
}

const ReportMasterDetailContext = createContext<ReportMasterDetailContextValue | undefined>(undefined);

export const ReportMasterDetailProvider = ({
  reportId,
  groupId,
  children,
}: {
  reportId: string | undefined;
  groupId: string | undefined;
  children: React.ReactNode;
}) => {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const previousGroupId = useRef(groupId);

  const selectedAssetId = searchParameters.get(ASSET_ID_PARAM) ?? undefined;

  const setSelectedAssetId = useCallback(
    (id: string | undefined) => {
      setSearchParameters(
        previous => {
          const next = new URLSearchParams(previous);
          if (id) {
            next.set(ASSET_ID_PARAM, id);
          } else {
            next.delete(ASSET_ID_PARAM);
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParameters]
  );

  useEffect(() => {
    if (previousGroupId.current !== groupId) {
      previousGroupId.current = groupId;
      setSelectedAssetId(undefined);
    }
  }, [groupId, setSelectedAssetId]);

  const {
    assets: allAssets,
    total: allAssetsTotal,
    isLoading: isLoadingAllAssets,
    error: allAssetsError,
  } = useInfiniteReportGroupAssets({
    reportId,
    groupId,
    enabled: Boolean(reportId && groupId),
  });

  const hasMatchData = !isLoadingAllAssets && allAssets.some(a => a.matches && a.matches.length > 0);

  const value = useMemo(
    () => ({
      selectedAssetId,
      setSelectedAssetId,
      hasMatchData,
      allAssets,
      allAssetsTotal,
      isLoadingAllAssets,
      allAssetsError,
    }),
    [selectedAssetId, setSelectedAssetId, hasMatchData, allAssets, allAssetsTotal, isLoadingAllAssets, allAssetsError]
  );

  return <ReportMasterDetailContext.Provider value={value}>{children}</ReportMasterDetailContext.Provider>;
};

export const useReportMasterDetail = () => {
  const context = useContext(ReportMasterDetailContext);
  if (!context) {
    throw new Error('useReportMasterDetail must be used within ReportMasterDetailProvider');
  }
  return context;
};
